"use client";

import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";
import { PanelLeft } from "lucide-react";
import * as React from "react";

import { useIsMobile } from "@/components/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContextProps = {
    state: "expanded" | "collapsed";
    open: boolean;
    setOpen: (open: boolean) => void;
    openMobile: boolean;
    setOpenMobile: (open: boolean) => void;
    isMobile: boolean;
    toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
    const context = React.useContext(SidebarContext);
    if (!context) {
        throw new Error("useSidebar must be used within a SidebarProvider.");
    }

    return context;
}

const SidebarProvider = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<"div"> & {
        defaultOpen?: boolean;
        open?: boolean;
        onOpenChange?: (open: boolean) => void;
    }
>(({ defaultOpen = true, open: openProp, onOpenChange: setOpenProp, className, style, children, ...props }, ref) => {
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = React.useState(false);

    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp ?? _open;
    const setOpen = React.useCallback(
        (value: boolean | ((value: boolean) => boolean)) => {
            const openState = typeof value === "function" ? value(open) : value;
            if (setOpenProp) {
                setOpenProp(openState);
            } else {
                _setOpen(openState);
            }

            // This sets the cookie to keep the sidebar state.
            document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
        },
        [setOpenProp, open]
    );

    // Helper to toggle the sidebar.
    const toggleSidebar = React.useCallback(() => {
        return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
    }, [isMobile, setOpen, setOpenMobile]);

    // Adds a keyboard shortcut to toggle the sidebar.
    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggleSidebar();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const state = open ? "expanded" : "collapsed";

    const contextValue = React.useMemo<SidebarContextProps>(
        () => ({
            state,
            open,
            setOpen,
            isMobile,
            openMobile,
            setOpenMobile,
            toggleSidebar,
        }),
        [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
    );

    return (
        <SidebarContext.Provider value={contextValue}>
            <TooltipProvider delayDuration={0}>
                <div
                    style={
                        {
                            "--sidebar-width": SIDEBAR_WIDTH,
                            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
                            ...style,
                        } as React.CSSProperties
                    }
                    className={cn(
                        "setu:group/sidebar-wrapper setu:flex setu:min-h-svh setu:w-full setu:has-data-[variant=inset]:bg-sidebar",
                        className
                    )}
                    ref={ref}
                    {...props}
                >
                    {children}
                </div>
            </TooltipProvider>
        </SidebarContext.Provider>
    );
});
SidebarProvider.displayName = "SidebarProvider";

const Sidebar = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<"div"> & {
        side?: "left" | "right";
        variant?: "sidebar" | "floating" | "inset";
        collapsible?: "offcanvas" | "icon" | "none";
    }
>(({ side = "left", variant = "sidebar", collapsible = "offcanvas", className, children, ...props }, ref) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

    if (collapsible === "none") {
        return (
            <div
                className={cn(
                    "setu:flex setu:h-full setu:w-(--sidebar-width) setu:flex-col setu:bg-sidebar setu:text-sidebar-foreground",
                    className
                )}
                ref={ref}
                {...props}
            >
                {children}
            </div>
        );
    }

    if (isMobile) {
        return (
            <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
                <SheetContent
                    data-sidebar="sidebar"
                    data-mobile="true"
                    className="setu:w-(--sidebar-width) setu:bg-sidebar setu:p-0 setu:text-sidebar-foreground setu:[&>button]:hidden"
                    style={
                        {
                            "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
                        } as React.CSSProperties
                    }
                    side={side}
                >
                    <SheetHeader className="sr-only">
                        <SheetTitle>Sidebar</SheetTitle>
                        <SheetDescription>Displays the mobile sidebar.</SheetDescription>
                    </SheetHeader>
                    <div className="setu:flex setu:h-full setu:w-full setu:flex-col">{children}</div>
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <div
            ref={ref}
            className="setu:group setu:peer setu:hidden setu:text-sidebar-foreground setu:md:block"
            data-state={state}
            data-collapsible={state === "collapsed" ? collapsible : ""}
            data-variant={variant}
            data-side={side}
        >
            {/* This is what handles the sidebar gap on desktop */}
            <div
                className={cn(
                    "setu:relative setu:w-(--sidebar-width) setu:bg-transparent setu:transition-[width] setu:duration-200 setu:ease-linear",
                    "setu:group-data-[collapsible=offcanvas]:w-0",
                    "setu:group-data-[side=right]:rotate-180",
                    variant === "floating" || variant === "inset"
                        ? "setu:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
                        : "setu:group-data-[collapsible=icon]:w-(--sidebar-width-icon)"
                )}
            />
            <div
                className={cn(
                    "setu:fixed setu:inset-y-0 setu:z-10 setu:hidden setu:h-svh setu:w-(--sidebar-width) setu:transition-[left,right,width] setu:duration-200 setu:ease-linear setu:md:flex",
                    side === "left"
                        ? "setu:left-0 setu:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
                        : "setu:right-0 setu:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
                    // Adjust the padding for floating and inset variants.
                    variant === "floating" || variant === "inset"
                        ? "setu:p-2 setu:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
                        : "setu:group-data-[collapsible=icon]:w-(--sidebar-width-icon) setu:group-data-[side=left]:!border-r setu:group-data-[side=right]:!border-l",
                    className
                )}
                {...props}
            >
                <div
                    data-sidebar="sidebar"
                    className="setu:flex setu:h-full setu:w-full setu:flex-col setu:bg-sidebar setu:group-data-[variant=floating]:rounded-lg setu:group-data-[variant=floating]:!border setu:group-data-[variant=floating]:!border-sidebar-border setu:group-data-[variant=floating]:shadow-sm"
                >
                    {children}
                </div>
            </div>
        </div>
    );
});
Sidebar.displayName = "Sidebar";

const SidebarTrigger = React.forwardRef<React.ElementRef<typeof Button>, React.ComponentProps<typeof Button>>(
    ({ className, onClick, ...props }, ref) => {
        const { toggleSidebar } = useSidebar();

        return (
            <Button
                ref={ref}
                data-sidebar="trigger"
                variant="ghost"
                size="icon"
                className={cn("setu:h-7 setu:w-7", className)}
                onClick={(event) => {
                    onClick?.(event);
                    toggleSidebar();
                }}
                {...props}
            >
                <PanelLeft />
                <span className="setu:sr-only">Toggle Sidebar</span>
            </Button>
        );
    }
);
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarRail = React.forwardRef<HTMLButtonElement, React.ComponentProps<"button">>(
    ({ className, ...props }, ref) => {
        const { toggleSidebar } = useSidebar();

        return (
            <button
                ref={ref}
                data-sidebar="rail"
                aria-label="Toggle Sidebar"
                tabIndex={-1}
                onClick={toggleSidebar}
                title="Toggle Sidebar"
                className={cn(
                    "setu:absolute setu:inset-y-0 setu:z-20 setu:hidden setu:w-4 setu:-translate-x-1/2 setu:transition-all setu:ease-linear setu:after:absolute setu:after:inset-y-0 setu:after:left-1/2 setu:after:w-[2px] setu:hover:after:!bg-sidebar-border setu:group-data-[side=left]:-right-4 setu:group-data-[side=right]:left-0 setu:sm:flex",
                    "setu:in-data-[side=left]:cursor-w-resize setu:in-data-[side=right]:cursor-e-resize",
                    "setu:[[data-side=left][data-state=collapsed]_&]:cursor-e-resize setu:[[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
                    "setu:group-data-[collapsible=offcanvas]:translate-x-0 setu:group-data-[collapsible=offcanvas]:after:left-full setu:hover:group-data-[collapsible=offcanvas]:bg-sidebar",
                    "setu:[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
                    "setu:[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
                    className
                )}
                {...props}
            />
        );
    }
);
SidebarRail.displayName = "SidebarRail";

const SidebarInset = React.forwardRef<HTMLDivElement, React.ComponentProps<"main">>(({ className, ...props }, ref) => {
    return (
        <main
            ref={ref}
            className={cn(
                "setu:relative setu:flex setu:w-full setu:flex-1 setu:flex-col setu:bg-background",
                "setu:md:peer-data-[variant=inset]:m-2 setu:md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 setu:md:peer-data-[variant=inset]:ml-0 setu:md:peer-data-[variant=inset]:rounded-xl setu:md:peer-data-[variant=inset]:shadow-sm",
                className
            )}
            {...props}
        />
    );
});
SidebarInset.displayName = "SidebarInset";

const SidebarInput = React.forwardRef<React.ElementRef<typeof Input>, React.ComponentProps<typeof Input>>(
    ({ className, ...props }, ref) => {
        return (
            <Input
                ref={ref}
                data-sidebar="input"
                className={cn(
                    "setu:h-8 setu:w-full setu:bg-background setu:shadow-none setu:focus-visible:ring-2 setu:focus-visible:ring-sidebar-ring",
                    className
                )}
                {...props}
            />
        );
    }
);
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(({ className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            data-sidebar="header"
            className={cn("setu:flex setu:flex-col setu:gap-2 setu:p-2", className)}
            {...props}
        />
    );
});
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(({ className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            data-sidebar="footer"
            className={cn("setu:flex setu:flex-col setu:gap-2 setu:p-2", className)}
            {...props}
        />
    );
});
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = React.forwardRef<React.ElementRef<typeof Separator>, React.ComponentProps<typeof Separator>>(
    ({ className, ...props }, ref) => {
        return (
            <Separator
                ref={ref}
                data-sidebar="separator"
                className={cn("setu:mx-2 setu:w-auto setu:!bg-sidebar-border", className)}
                {...props}
            />
        );
    }
);
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(({ className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            data-sidebar="content"
            className={cn(
                "setu:flex setu:min-h-0 setu:flex-1 setu:flex-col setu:gap-2 setu:overflow-auto setu:group-data-[collapsible=icon]:overflow-hidden",
                className
            )}
            {...props}
        />
    );
});
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(({ className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            data-sidebar="group"
            className={cn("setu:relative setu:flex setu:w-full setu:min-w-0 setu:flex-col setu:p-2", className)}
            {...props}
        />
    );
});
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = React.forwardRef<HTMLDivElement, React.ComponentProps<"div"> & { asChild?: boolean }>(
    ({ className, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "div";

        return (
            <Comp
                ref={ref}
                data-sidebar="group-label"
                className={cn(
                    "setu:flex setu:h-8 setu:shrink-0 setu:items-center setu:rounded-md setu:px-2 setu:text-xs setu:font-medium setu:text-sidebar-foreground/70 setu:outline-hidden setu:ring-sidebar-ring setu:transition-[margin,opacity] setu:duration-200 setu:ease-linear setu:focus-visible:ring-2 setu:[&>svg]:size-4 setu:[&>svg]:shrink-0",
                    "setu:group-data-[collapsible=icon]:-mt-8 setu:group-data-[collapsible=icon]:opacity-0",
                    className
                )}
                {...props}
            />
        );
    }
);
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupAction = React.forwardRef<HTMLButtonElement, React.ComponentProps<"button"> & { asChild?: boolean }>(
    ({ className, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";

        return (
            <Comp
                ref={ref}
                data-sidebar="group-action"
                className={cn(
                    "setu:absolute setu:right-3 setu:top-3.5 setu:flex setu:aspect-square setu:w-5 setu:items-center setu:justify-center setu:rounded-md setu:p-0 setu:text-sidebar-foreground setu:outline-hidden setu:ring-sidebar-ring setu:transition-transform setu:hover:bg-sidebar-accent setu:hover:text-sidebar-accent-foreground setu:focus-visible:ring-2 setu:[&>svg]:size-4 setu:[&>svg]:shrink-0",
                    // Increases the hit area of the button on mobile.
                    "setu:after:absolute setu:after:-inset-2 setu:md:after:hidden",
                    "setu:group-data-[collapsible=icon]:hidden",
                    className
                )}
                {...props}
            />
        );
    }
);
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
    ({ className, ...props }, ref) => (
        <div ref={ref} data-sidebar="group-content" className={cn("setu:w-full setu:text-sm", className)} {...props} />
    )
);
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(({ className, ...props }, ref) => (
    <ul
        ref={ref}
        data-sidebar="menu"
        className={cn("setu:flex setu:w-full setu:min-w-0 setu:flex-col setu:gap-1", className)}
        {...props}
    />
));
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(({ className, ...props }, ref) => (
    <li ref={ref} data-sidebar="menu-item" className={cn("setu:group/menu-item setu:relative", className)} {...props} />
));
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva(
    "setu:peer/menu-button setu:flex setu:w-full setu:items-center setu:gap-2 setu:overflow-hidden setu:rounded-md setu:p-2 setu:text-left setu:text-sm setu:outline-hidden setu:ring-sidebar-ring setu:transition-[width,height,padding] setu:hover:bg-sidebar-accent setu:hover:text-sidebar-accent-foreground setu:focus-visible:ring-2 setu:active:bg-sidebar-accent setu:active:text-sidebar-accent-foreground setu:disabled:pointer-events-none setu:disabled:opacity-50 setu:group-has-data-[sidebar=menu-action]/menu-item:pr-8 setu:aria-disabled:pointer-events-none setu:aria-disabled:opacity-50 setu:data-[active=true]:bg-sidebar-accent setu:data-[active=true]:font-medium setu:data-[active=true]:text-sidebar-accent-foreground setu:data-[state=open]:hover:bg-sidebar-accent setu:data-[state=open]:hover:text-sidebar-accent-foreground setu:group-data-[collapsible=icon]:size-8! setu:group-data-[collapsible=icon]:p-2! setu:[&>span:last-child]:truncate setu:[&>svg]:size-4 setu:[&>svg]:shrink-0",
    {
        variants: {
            variant: {
                default: "setu:hover:bg-sidebar-accent setu:hover:text-sidebar-accent-foreground",
                outline:
                    "setu:bg-background setu:shadow-[0_0_0_1px_hsl(var(--sidebar-border))] setu:hover:bg-sidebar-accent setu:hover:text-sidebar-accent-foreground setu:hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
            },
            size: {
                default: "setu:h-8 setu:text-sm",
                sm: "setu:h-7 setu:text-xs",
                lg: "setu:h-12 setu:text-sm setu:group-data-[collapsible=icon]:p-0!",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

const SidebarMenuButton = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<"button"> & {
        asChild?: boolean;
        isActive?: boolean;
        tooltip?: string | React.ComponentProps<typeof TooltipContent>;
    } & VariantProps<typeof sidebarMenuButtonVariants>
>(({ asChild = false, isActive = false, variant = "default", size = "default", tooltip, className, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const { isMobile, state } = useSidebar();

    const button = (
        <Comp
            ref={ref}
            data-sidebar="menu-button"
            data-size={size}
            data-active={isActive}
            className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
            {...props}
        />
    );

    if (!tooltip) {
        return button;
    }

    if (typeof tooltip === "string") {
        tooltip = {
            children: tooltip,
        };
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side="right" align="center" hidden={state !== "collapsed" || isMobile} {...tooltip} />
        </Tooltip>
    );
});
SidebarMenuButton.displayName = "SidebarMenuButton";

const SidebarMenuAction = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<"button"> & {
        asChild?: boolean;
        showOnHover?: boolean;
    }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
        <Comp
            ref={ref}
            data-sidebar="menu-action"
            className={cn(
                "setu:absolute setu:right-1 setu:top-1.5 setu:flex setu:aspect-square setu:w-5 setu:items-center setu:justify-center setu:rounded-md setu:p-0 setu:text-sidebar-foreground setu:outline-hidden setu:ring-sidebar-ring setu:transition-transform setu:hover:bg-sidebar-accent setu:hover:text-sidebar-accent-foreground setu:focus-visible:ring-2 setu:peer-hover/menu-button:text-sidebar-accent-foreground setu:[&>svg]:size-4 setu:[&>svg]:shrink-0",
                // Increases the hit area of the button on mobile.
                "setu:after:absolute setu:after:-inset-2 setu:md:after:hidden",
                "setu:peer-data-[size=sm]/menu-button:top-1",
                "setu:peer-data-[size=default]/menu-button:top-1.5",
                "setu:peer-data-[size=lg]/menu-button:top-2.5",
                "setu:group-data-[collapsible=icon]:hidden",
                showOnHover &&
                    "setu:group-focus-within/menu-item:opacity-100 setu:group-hover/menu-item:opacity-100 setu:data-[state=open]:opacity-100 setu:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground setu:md:opacity-0",
                className
            )}
            {...props}
        />
    );
});
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            data-sidebar="menu-badge"
            className={cn(
                "setu:pointer-events-none setu:absolute setu:right-1 setu:flex setu:h-5 setu:min-w-5 setu:select-none setu:items-center setu:justify-center setu:rounded-md setu:px-1 setu:text-xs setu:font-medium setu:tabular-nums setu:text-sidebar-foreground",
                "setu:peer-hover/menu-button:text-sidebar-accent-foreground setu:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
                "setu:peer-data-[size=sm]/menu-button:top-1",
                "setu:peer-data-[size=default]/menu-button:top-1.5",
                "setu:peer-data-[size=lg]/menu-button:top-2.5",
                "setu:group-data-[collapsible=icon]:hidden",
                className
            )}
            {...props}
        />
    )
);
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<"div"> & {
        showIcon?: boolean;
    }
>(({ className, showIcon = false, ...props }, ref) => {
    // Random width between 50 to 90%.
    const width = React.useMemo(() => {
        return `${Math.floor(Math.random() * 40) + 50}%`;
    }, []);

    return (
        <div
            ref={ref}
            data-sidebar="menu-skeleton"
            className={cn("setu:flex setu:h-8 setu:items-center setu:gap-2 setu:rounded-md setu:px-2", className)}
            {...props}
        >
            {showIcon && <Skeleton className="setu:size-4 setu:rounded-md" data-sidebar="menu-skeleton-icon" />}
            <Skeleton
                className="setu:h-4 setu:max-w-(--skeleton-width) setu:flex-1"
                data-sidebar="menu-skeleton-text"
                style={
                    {
                        "--skeleton-width": width,
                    } as React.CSSProperties
                }
            />
        </div>
    );
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(
    ({ className, ...props }, ref) => (
        <ul
            ref={ref}
            data-sidebar="menu-sub"
            className={cn(
                "setu:mx-3.5 setu:flex setu:min-w-0 setu:translate-x-px setu:flex-col setu:gap-1 setu:!border-l setu:!border-sidebar-border setu:px-2.5 setu:py-0.5",
                "setu:group-data-[collapsible=icon]:hidden",
                className
            )}
            {...props}
        />
    )
);
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(({ ...props }, ref) => (
    <li ref={ref} {...props} />
));
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = React.forwardRef<
    HTMLAnchorElement,
    React.ComponentProps<"a"> & {
        asChild?: boolean;
        size?: "sm" | "md";
        isActive?: boolean;
    }
>(({ asChild = false, size = "md", isActive, className, ...props }, ref) => {
    const Comp = asChild ? Slot : "a";

    return (
        <Comp
            ref={ref}
            data-sidebar="menu-sub-button"
            data-size={size}
            data-active={isActive}
            className={cn(
                "setu:flex setu:h-7 setu:min-w-0 setu:-translate-x-px setu:items-center setu:gap-2 setu:overflow-hidden setu:rounded-md setu:px-2 setu:text-sidebar-foreground setu:outline-hidden setu:ring-sidebar-ring setu:hover:bg-sidebar-accent setu:hover:text-sidebar-accent-foreground setu:focus-visible:ring-2 setu:active:bg-sidebar-accent setu:active:text-sidebar-accent-foreground setu:disabled:pointer-events-none setu:disabled:opacity-50 setu:aria-disabled:pointer-events-none setu:aria-disabled:opacity-50 setu:[&>span:last-child]:truncate setu:[&>svg]:size-4 setu:[&>svg]:shrink-0 setu:[&>svg]:text-sidebar-accent-foreground",
                "setu:data-[active=true]:bg-sidebar-accent setu:data-[active=true]:text-sidebar-accent-foreground",
                size === "sm" && "text-xs",
                size === "md" && "text-sm",
                "setu:group-data-[collapsible=icon]:hidden",
                className
            )}
            {...props}
        />
    );
});
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupAction,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInput,
    SidebarInset,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSkeleton,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarProvider,
    SidebarRail,
    SidebarSeparator,
    SidebarTrigger,
    useSidebar,
};
